import { useNavigate } from "react-router-dom";
import { MessageCircleQuestion, Flag, ClipboardCheck, FileWarning, Radar } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Reveal, Tag, H2, GoldBtn, scrollToId, BrushHighlight } from "@/components/sections/shared";
import FoldText from "@/components/FoldText/FoldText";

const magnets = [
  { icon: MessageCircleQuestion, title: "Ask Jack", copy: "Your first question is the fastest way to get personal guidance.", live: true, action: "ask" },
  { icon: Flag, title: "Report A Denied Or Lowballed Claim", copy: "Submit your situation so Inssux can understand what happened and guide the next step.", live: true, target: "community" },
  { icon: ClipboardCheck, title: "Bad-Faith Denial Checklist", copy: "A printable checklist to help you review your denial letter.", live: false },
  { icon: FileWarning, title: "10 Bad-Faith Tactics Every Insurance Carrier Uses", copy: "A plain-English guide to common tactics policyholders should know.", live: false },
  { icon: Radar, title: "Rip-Off Detector", copy: "Answer 5 quick questions and get a plain-English Claim Check summary.", live: true, href: "/rip-off-detector" },
];

const faqs = [
  { q: "Is Jack a lawyer?", a: "No. Jack is not a lawyer and does not provide legal advice." },
  { q: "Is Inssux an insurance company?", a: "No. Inssux is not an insurance company. Inssux helps policyholders better understand insurance claim situations." },
  { q: "Is Inssux a public adjusting firm?", a: "No. Inssux is not a licensed public adjusting firm. Jack provides educational guidance and may recommend when professional help should be considered." },
  { q: "Can Jack guarantee my claim will be paid?", a: "No. Jack does not guarantee claim outcomes. Jack helps you understand your options, possible red flags, and next logical steps." },
  { q: "What kind of claims can Jack help with?", a: "Jack can help users understand situations involving denied, delayed, confusing, or underpaid claims across several categories, with a strong focus on property, homeowners, disaster, auto, and business claims." },
  { q: "What happens after I ask Jack a question?", a: "You receive guidance and recommended next steps. If appropriate, you may be invited to continue with Ask Jack Premium or other advanced claim assistance options." },
  { q: "Do I need to upload private documents?", a: "Not always. You can begin by describing what happened. More advanced tools may allow you to paste claim language or provide more details." },
  { q: "Is my information shared publicly?", a: "No claim story should be shared publicly without clear permission. Claim reports should be used to help Inssux understand patterns, qualify leads, and provide better support." },
];

export const LeadMagnets = () => {
  const navigate = useNavigate();

  return (
  <section id="resources" data-testid="resources-section" className="bg-[#121214] py-24 md:py-32">
    <div className="max-w-[1080px] mx-auto px-6 lg:px-14">
      <Reveal>
        <Tag testId="resources-tag">Free Tools</Tag>
        <H2 className="max-w-2xl">
          <FoldText
            text="Before you accept the insurance company's answer,"
            splitBy="word"
            trigger="scroll"
            fontSize="inherit"
            fontWeight="inherit"
            color="inherit"
          />{" "}
          <FoldText
            text="check the red flags."
            splitBy="word"
            trigger="scroll"
            fontSize="inherit"
            fontWeight="inherit"
            color="#E63946"
          />
        </H2>
        <p className="mt-4 font-serif-jack italic text-xl text-[#A1A1AA]">
          Get simple tools that help you understand what may be happening in your claim.
        </p>
      </Reveal>
      <div className="mt-12 space-y-4">
        {magnets.map((m, i) => (
          <Reveal key={m.title} delay={i * 0.06}>
            <button
              data-testid={`magnet-row-${i}`}
              onClick={
                m.action === "ask"
                  ? () => window.AIWidget?.ask()
                  : m.href
                  ? () => navigate(m.href)
                  : m.target
                  ? () => scrollToId(m.target)
                  : undefined
              }
              className={`w-full text-left flex items-start sm:items-center gap-5 bg-[#0A0A0C] border p-6 transition-colors duration-300 ${
                m.live ? "border-[#8A2BE2]/40 hover:border-[#9D4EDD]" : "border-white/10 cursor-default"
              }`}
            >
              <m.icon size={22} className={m.live ? "text-[#FFD166] shrink-0" : "text-[#71717A] shrink-0"} />
              <div className="flex-1">
                <h3 className={`font-display text-base tracking-wide ${m.live ? "text-white" : "text-white/70"}`}>{m.title}</h3>
                <p className="mt-1 text-sm text-[#A1A1AA] font-light">{m.copy}</p>
              </div>
              <span
                className={`shrink-0 font-mono-file text-[9px] tracking-[0.2em] px-2.5 py-1 border ${
                  m.live ? "text-[#FFD166] border-[#FFD166]/40" : "text-[#71717A] border-white/10"
                }`}
              >
                {m.live ? "LIVE" : "COMING SOON"}
              </span>
            </button>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.3}>
        <div className="mt-11">
          <GoldBtn testId="resources-cta" onClick={() => navigate("/rip-off-detector")}>
            TAKE THE <BrushHighlight>FREE</BrushHighlight> RIP-OFF DETECTOR
          </GoldBtn>
        </div>
      </Reveal>
    </div>
  </section>
  );
};

export const FAQ = () => (
  <section id="faq" data-testid="faq-section" className="bg-[#0A0A0C] py-24 md:py-32">
    <div className="max-w-[880px] mx-auto px-6 lg:px-14">
      <Reveal>
        <Tag testId="faq-tag">FAQ</Tag>
        <H2>
          <FoldText
            text="Common questions"
            splitBy="word"
            trigger="scroll"
            fontSize="inherit"
            fontWeight="inherit"
            color="inherit"
          />
        </H2>
      </Reveal>
      <Reveal delay={0.15}>
        <Accordion type="single" collapsible className="mt-10">
          {faqs.map((f, i) => (
            <AccordionItem key={f.q} value={`faq-${i}`} className="border-white/10">
              <AccordionTrigger data-testid={`faq-trigger-${i}`} className="text-left font-display tracking-wide text-white hover:text-[#FFD166] hover:no-underline py-5">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-[#A1A1AA] font-light leading-relaxed">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Reveal>
    </div>
  </section>
);
